import React, { useMemo, useState } from 'react';
import { ExternalLink } from '@saleshandy/icons';
import {
  OverlayPopover,
  Placement,
} from '../../../../../shared/design-system/components/overlay';
import { getPlatformType, ICON_MAP } from './socials-cell';
import { accessibleOnClick } from '../../../../../shared/utils';

const SOCIAL_PLATFORM_NAMES: Record<string, string> = {
  linkedin: 'LinkedIn',
  facebook: 'Facebook',
  twitter: 'Twitter',
  github: 'GitHub',
  reddit: 'Reddit',
  discord: 'Discord',
  youtube: 'YouTube',
  instagram: 'Instagram',
  pinterest: 'Pinterest',
  crunchbase: 'Crunchbase',
  generic: 'Website',
};

interface CompanySocialsCellProps {
  socialUrls?: string[] | null;
  placement?: Placement;
}

const CompanySocialsCell: React.FC<CompanySocialsCellProps> = ({
  socialUrls,
  placement = Placement.Bottom,
}) => {
  const [showSocialsPopover, setShowSocialsPopover] = useState(false);

  const uniqueLinks = useMemo(() => {
    if (!socialUrls || socialUrls.length === 0) return [];
    return socialUrls
      .filter((url): url is string => Boolean(url))
      .map((url) => ({ type: getPlatformType(url), url }))
      .filter(
        (entry, index, array) =>
          entry.url &&
          array.findIndex(
            (item) => item.url.toLowerCase() === entry.url.toLowerCase(),
          ) === index,
      );
  }, [socialUrls]);

  if (uniqueLinks.length === 0)
    {return <div className="v2-lead-socials-cell">-</div>;}

  const MAX_VISIBLE = 4;
  const VISIBLE_WITH_CHIP = 3;

  const visibleLinks =
    uniqueLinks.length > MAX_VISIBLE
      ? uniqueLinks.slice(0, VISIBLE_WITH_CHIP)
      : uniqueLinks;
  const hiddenLinks =
    uniqueLinks.length > MAX_VISIBLE ? uniqueLinks.slice(VISIBLE_WITH_CHIP) : [];

  const socialsPopoverContent = (
    <div className="v2-lead-socials-popover">
      {hiddenLinks.map((entry) => {
        const IconComponent = ICON_MAP[entry.type] || ICON_MAP.generic;
        const platformName = SOCIAL_PLATFORM_NAMES[entry.type] || entry.type;
        return (
          <a
            key={entry.url}
            href={entry.url}
            target="_blank"
            rel="noreferrer"
            className="v2-lead-socials-popover-item"
            onClick={(e) => e.stopPropagation()}
          >
            <IconComponent width={20} height={20} />
            <span className="v2-lead-socials-popover-name">{platformName}</span>
            <ExternalLink width={14} height={14} color="#6b7280" />
          </a>
        );
      })}
    </div>
  );

  return (
    <div className="v2-lead-socials-cell">
      {visibleLinks.map((entry) => {
        const IconComponent = ICON_MAP[entry.type] || ICON_MAP.generic;
        return (
          <a
            key={entry.url}
            href={entry.url}
            target="_blank"
            rel="noreferrer"
            className="v2-lead-social-link"
            onClick={(e) => e.stopPropagation()}
          >
            <IconComponent width={20} height={20} />
          </a>
        );
      })}
      {hiddenLinks.length > 0 && (
        <OverlayPopover
          show={showSocialsPopover}
          onToggle={setShowSocialsPopover}
          placement={placement}
          className="v2-lead-socials-more-popover"
          content={socialsPopoverContent}
          rootClose
        >
          <span
            className={`v2-company-count-pill ${showSocialsPopover ? 'active' : ''}`}
            {...accessibleOnClick((e) => {
              e.stopPropagation();
              setShowSocialsPopover(!showSocialsPopover);
            })}
          >
            +{hiddenLinks.length}
          </span>
        </OverlayPopover>
      )}
    </div>
  );
};

export default CompanySocialsCell;
